import type { FastifyRequest } from 'fastify'
import { extractIp } from './helpers'
import { createRateLimiter } from './middleware'
import type { FastifyRateLimiterInstance } from './middleware'
import type { RateLimitOptions } from '../../types'

// ── Strategy aliases ───────────────────────────────────────────────────────────

export const createFixedRateLimiter = (
  options: Omit<RateLimitOptions<FastifyRequest>, 'strategy'> = {}
): FastifyRateLimiterInstance =>
  createRateLimiter({ ...options, strategy: 'fixed' })

export const createSlidingRateLimiter = (
  options: Omit<RateLimitOptions<FastifyRequest>, 'strategy'> = {}
): FastifyRateLimiterInstance =>
  createRateLimiter({ ...options, strategy: 'sliding' })

// ── Endpoint-scoped factories ──────────────────────────────────────────────────

/**
 * Limiter whose key is prefixed with the endpoint name for per-route isolation.
 *
 * @example
 * ```ts
 * app.post('/api/login', {
 *   onRequest: createEndpointRateLimiter('/api/login', { maxRequests: 5 })
 * }, loginHandler)
 * ```
 */
export const createEndpointRateLimiter = (
  endpoint: string,
  options: RateLimitOptions<FastifyRequest> = {}
): FastifyRateLimiterInstance =>
  createRateLimiter({
    ...options,
    keyGenerator: (req: FastifyRequest) => {
      const base = options.keyGenerator
        ? options.keyGenerator(req)
        : extractIp(req)
      return `${endpoint}:${base}`
    }
  })

export const createSlidingEndpointRateLimiter = (
  endpoint: string,
  options: Omit<RateLimitOptions<FastifyRequest>, 'strategy'> = {}
): FastifyRateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: (req: FastifyRequest) => {
      const base = options.keyGenerator
        ? options.keyGenerator(req)
        : extractIp(req)
      return `${endpoint}:${base}`
    }
  })
